import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ControlContainer, UntypedFormControl, FormGroupDirective } from '@angular/forms';
import { OwnerFieldMap } from '@app/modules/statements/models/statements.models';
import { StatementsAccountingIntegrationService } from '@app/modules/statements/services/statements-accounting-integration.service';
import { Observable } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

@Component({
  selector: 'sbnb-statements-accounting-connection-form-owners',
  templateUrl: './statements-accounting-connection-form-owners.component.html',
  styles: [],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementsAccountingConnectionFormOwnersComponent implements OnInit {
  @Input() eligibleOwners: OwnerFieldMap[];

  public formState$ = this.statementsAccountingIntegrationService.formState$;

  // Get supplier options from form state
  public suppliers$ = this.formState$.pipe(
    map((state) => state.data?.owners.options.suppliers || []),
    shareReplay(1)
  );

  // Get customer options from form state
  public customers$ = this.formState$.pipe(
    map((state) => state.data?.owners.options.customers || []),
    shareReplay(1)
  );

  public availableSuppliersSearchCtrl = new UntypedFormControl();
  public availableCustomersSearchCtrl = new UntypedFormControl();

  public filteredAvailableSuppliers$ = this.availableSuppliersSearchCtrl.valueChanges.pipe(
    startWith(''),
    debounceTime(300),
    switchMap((searchValue) =>
      this.suppliers$.pipe(
        map((suppliers) =>
          suppliers.filter(
            (supplier) =>
              !searchValue ||
              supplier.name
                .toLowerCase()
                .replace(/[-_]/g, ' ')
                .includes(searchValue.toLowerCase().replace(/[-_]/g, ' '))
          )
        )
      )
    )
  );

  public filteredAvailableCustomers$ = this.availableCustomersSearchCtrl.valueChanges.pipe(
    startWith(''),
    debounceTime(300),
    switchMap((searchValue) =>
      this.customers$.pipe(
        map((customers) =>
          customers.filter(
            (customer) =>
              !searchValue ||
              customer.name
                .toLowerCase()
                .replace(/[-_]/g, ' ')
                .includes(searchValue.toLowerCase().replace(/[-_]/g, ' '))
          )
        )
      )
    )
  );

  constructor(
    private statementsAccountingIntegrationService: StatementsAccountingIntegrationService,
    public form: FormGroupDirective
  ) {}

  ngOnInit(): void {}

  // Get the default value for an owner
  public getDefaultValue$(ownerId: number) {
    return this.formState$.pipe(
      map((state) => {
        const fieldData = state.data?.owners.fieldMap[ownerId];
        return fieldData?.isDefault && fieldData?.value ? fieldData?.value : null;
      }),
      shareReplay(1)
    );
  }

  // Get the selected entity (supplier or customer) for an owner
  public selectedEntity(ownerId: number): Observable<{ id: number; name: string }> {
    const owner = this.eligibleOwners.find((o) => o.id === ownerId);
    const selectedId = this.form.form.get(['owners', ownerId]).value;

    if (!selectedId) return;

    return owner.externalType === 'customer'
      ? this.customers$.pipe(map((customers) => customers.find((c) => c.id === selectedId)))
      : this.suppliers$.pipe(map((suppliers) => suppliers.find((s) => s.id === selectedId)));
  }

  // Get available options for an owner based on their external type
  public getAvailableOptions$(ownerId: number): Observable<{ id: number; name: string }[]> {
    const owner = this.eligibleOwners.find((o) => o.id === ownerId);
    return owner.externalType === 'customer' ? this.filteredAvailableCustomers$ : this.filteredAvailableSuppliers$;
  }

  // Get the search control for an owner based on their external type
  public getSearchControl(ownerId: number): UntypedFormControl {
    const owner = this.eligibleOwners.find((o) => o.id === ownerId);
    return owner.externalType === 'customer' ? this.availableCustomersSearchCtrl : this.availableSuppliersSearchCtrl;
  }

  // Get the placeholder text for the search based on owner type
  public getSearchPlaceholder(ownerId: number): string {
    const owner = this.eligibleOwners.find((o) => o.id === ownerId);
    return owner.externalType === 'customer' ? 'Find customer...' : 'Find vendor...';
  }

  public getPlaceholder(ownerId: number): string {
    const owner = this.eligibleOwners.find((o) => o.id === ownerId);
    return owner.externalType === 'customer' ? 'customer' : 'vendor';
  }
}
